/**
 * Exceedios Custom Cart — Horizon port.
 *
 * Vanilla rewrite of the Dawn-era jQuery + Slick cart drawer. No dependencies.
 * Replaces Horizon's native <cart-drawer-component>.
 *
 * Integration notes:
 *  - Horizon syncs its header cart bubble off the `cart:update` DOM event
 *    (see assets/cart-icon.js). We listen to it so the drawer reacts to the
 *    theme's own add-to-cart, and we re-dispatch it after our own mutations so
 *    the bubble stays correct. Our own events carry source `adev-cart-drawer`
 *    so we ignore them on the way back in.
 *  - Some apps (bundles, one-click upsells, custom PDP forms) write to the cart
 *    without emitting `cart:update`. A fetch/XHR interceptor catches those too.
 *    Our own requests carry an `X-Adev` header so the interceptor skips them.
 *  - Section refresh uses the Section Rendering API on the ROOT path.
 *    `/cart?sections=` returns the cart JSON object instead of section HTML.
 */
(function () {
  'use strict';

  var SECTION_ID = 'adev-cart-drawer';
  var DRAWER_KEY = 'cart-drawer';
  var SP_STORAGE_KEY = 'adev_shipping_protection';
  var SP_TTL = 172800000; // 48h
  var QTY_DEBOUNCE = 400;

  var state = {
    scrollPosition: 0,
    upsellIndex: 0,
    refreshing: false,
    autoAddRunning: false,
    lastFocus: null
  };

  /* ------------------------------------------------------------------ utils */

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function rootUrl() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.replace(/\/$/, '');
  }
  function cartUrl(path) {
    return rootUrl() + path;
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments,
        ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }
  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function setItemWithExpiration(key, value, ttl) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ value: value, expiration: ttl ? new Date().getTime() + ttl : null })
      );
    } catch (e) {
      /* storage unavailable */
    }
  }
  function getItemWithExpiration(key) {
    try {
      var item = JSON.parse(localStorage.getItem(key));
      if (!item) return null;
      if (item.expiration && new Date().getTime() > item.expiration) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------------------------- dom lookups */

  function drawerEl() {
    return $('[data-adev-drawer="' + DRAWER_KEY + '"]');
  }
  function overlayEl() {
    return $('[data-adev-drawer-overlay]');
  }
  function contentsEl() {
    return $('[data-adev-cart-drawer-contents]');
  }
  function footerEl() {
    return $('[data-adev-cart-drawer-footer]');
  }
  function scrollEl() {
    var d = drawerEl();
    if (!d) return null;
    return d.classList.contains('adev-drawer__full-scroll')
      ? $('.adev-cart-drawer__inner', d)
      : $('.adev-cart-drawer__items', d);
  }
  function cartCount() {
    var el = $('[data-adev-cart-count]');
    if (!el) return 0;
    return parseInt(el.getAttribute('data-adev-cart-count'), 10) || 0;
  }

  /* ------------------------------------------------------------ open / close */

  function measureFooter() {
    var f = footerEl();
    document.documentElement.style.setProperty(
      '--adev-cart-drawer-footer-height',
      (f ? f.offsetHeight : 0) + 'px'
    );
  }

  function openDrawer() {
    var d = drawerEl();
    if (!d || d.classList.contains('adev-opened')) return;
    state.lastFocus = document.activeElement;
    d.classList.add('adev-opened');
    d.setAttribute('tabindex', '0');
    d.setAttribute('aria-hidden', 'false');
    var o = overlayEl();
    if (o) o.classList.add('adev-opened');
    document.documentElement.classList.add('adev-noscroll');
    measureFooter();
    var closeBtn = $('[data-adev-close-drawer]', d);
    if (closeBtn) {
      try {
        closeBtn.focus({ preventScroll: true });
      } catch (e) {
        closeBtn.focus();
      }
    }
    document.dispatchEvent(new CustomEvent('adev:drawer:opened'));
  }

  function closeDrawer() {
    var d = drawerEl();
    if (!d) return;
    d.classList.remove('adev-opened');
    d.setAttribute('tabindex', '-1');
    d.setAttribute('aria-hidden', 'true');
    var o = overlayEl();
    if (o) o.classList.remove('adev-opened');
    document.documentElement.classList.remove('adev-noscroll');
    closeUpsellPopup();
    if (state.lastFocus && document.contains(state.lastFocus)) {
      try {
        state.lastFocus.focus({ preventScroll: true });
      } catch (e) {
        /* noop */
      }
    }
    state.lastFocus = null;
    document.dispatchEvent(new CustomEvent('adev:drawer:closed'));
  }

  function isOpen() {
    var d = drawerEl();
    return !!(d && d.classList.contains('adev-opened'));
  }

  function setLoading(on) {
    var c = contentsEl();
    if (!c) return;
    c.classList.toggle('loading', !!on);
  }

  /* --------------------------------------------------------- scroll position */

  function rememberScroll() {
    var el = scrollEl();
    if (el) state.scrollPosition = el.scrollTop;
  }
  function restoreScroll() {
    var el = scrollEl();
    if (el) el.scrollTop = state.scrollPosition;
  }

  /* ------------------------------------------------------------ cart requests */

  function cartRequest(path, body) {
    return fetch(cartUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Adev': '1'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || 'Cart request failed');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function withSections(body) {
    body = body || {};
    body.sections = SECTION_ID;
    body.sections_url = rootUrl() + '/';
    return body;
  }

  function fetchSection() {
    return fetch(rootUrl() + '/?sections=' + SECTION_ID + '&t=' + Date.now(), {
      method: 'GET',
      headers: { Accept: 'application/json', 'X-Adev': '1' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (d) {
        return d[SECTION_ID];
      });
  }

  /* ------------------------------------------------------------------ refresh */

  function applySectionHtml(html) {
    if (!html) return;
    var target = contentsEl();
    if (!target) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-adev-cart-drawer-contents]');
    if (!fresh) return;

    rememberScroll();
    target.innerHTML = fresh.innerHTML;

    restoreScroll();
    measureFooter();
    reinit();
    syncShippingProtectionState();
    syncBubble(cartCount());
  }

  function refresh(sections) {
    var html = sections && sections[SECTION_ID];
    var work = html ? Promise.resolve(html) : fetchSection();

    return work
      .then(function (h) {
        applySectionHtml(h);
        setLoading(false);
        return maybeAutoAddShippingProtection();
      })
      .catch(function (e) {
        setLoading(false);
        console.error('[adev-cart] refresh failed', e);
      });
  }

  /* ------------------------------------------------------- header bubble sync */

  function syncBubble(count) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: null,
          sourceId: SECTION_ID,
          data: { source: SECTION_ID, itemCount: count }
        }
      })
    );
  }

  /* ------------------------------------------------------- shipping protection */

  function syncShippingProtectionState() {
    var sp = (window.adev_shipping_protection = window.adev_shipping_protection || {});
    var toggle = $('[data-adev-shipping-protection-atc]');
    if (toggle) sp.alreadyAdded = toggle.classList.contains('active');
  }

  function maybeAutoAddShippingProtection() {
    var sp = window.adev_shipping_protection || {};
    if (!sp.enabled || !sp.productID) return Promise.resolve();
    if (sp.alreadyAdded || isDesignMode() || state.autoAddRunning) return Promise.resolve();
    if (getItemWithExpiration(SP_STORAGE_KEY) === 'true') return Promise.resolve();
    // Never auto-add protection to an empty cart.
    if ($$('[data-adev-cart-item]').length === 0) return Promise.resolve();

    state.autoAddRunning = true;
    setLoading(true);
    return cartRequest('/cart/add.js', withSections({ id: sp.productID, quantity: 1 }))
      .then(function (data) {
        setItemWithExpiration(SP_STORAGE_KEY, 'true', SP_TTL);
        sp.alreadyAdded = true;
        applySectionHtml(data.sections && data.sections[SECTION_ID]);
      })
      .catch(function (e) {
        console.error('[adev-cart] shipping protection auto-add failed', e);
      })
      .then(function () {
        state.autoAddRunning = false;
        setLoading(false);
      });
  }

  function toggleShippingProtection(btn) {
    var id = btn.getAttribute('data-id');
    if (!id) return;
    var turningOn = !btn.classList.contains('active');

    btn.classList.toggle('active', turningOn);
    btn.setAttribute('aria-checked', turningOn ? 'true' : 'false');
    setLoading(true);

    var req = turningOn
      ? cartRequest('/cart/add.js', withSections({ id: id, quantity: 1 }))
      : cartRequest('/cart/change.js', withSections({ id: id, quantity: 0 }));

    req
      .then(function (data) {
        // Manual choice wins: remember it so auto-add does not fight the shopper.
        setItemWithExpiration(SP_STORAGE_KEY, 'true', SP_TTL);
        return refresh(data.sections);
      })
      .catch(function (e) {
        btn.classList.toggle('active', !turningOn);
        btn.setAttribute('aria-checked', !turningOn ? 'true' : 'false');
        setLoading(false);
        console.error('[adev-cart] shipping protection toggle failed', e);
      });
  }

  /* ------------------------------------------------------------- line updates */

  var pushQuantities = debounce(function () {
    var form = $('[data-adev-cart-drawer-form]');
    if (!form) return;
    var updates = {};
    $$('input[name^="updates["]', form).forEach(function (input) {
      var key = input.name.slice('updates['.length, -1);
      var val = parseInt(input.value, 10);
      updates[key] = isNaN(val) ? 0 : Math.max(0, val);
    });
    if (!Object.keys(updates).length) return;

    setLoading(true);
    cartRequest('/cart/update.js', withSections({ updates: updates }))
      .then(function (data) {
        return refresh(data.sections);
      })
      .catch(function (e) {
        setLoading(false);
        console.error('[adev-cart] quantity update failed', e);
        refresh();
      });
  }, QTY_DEBOUNCE);

  function removeItem(key) {
    if (!key) return;
    rememberScroll();
    setLoading(true);
    cartRequest('/cart/change.js', withSections({ id: key, quantity: 0 }))
      .then(function (data) {
        return refresh(data.sections);
      })
      .catch(function (e) {
        setLoading(false);
        console.error('[adev-cart] remove failed', e);
      });
  }

  /* ------------------------------------------------------------ qty stepper */

  function stepQuantity(wrap, dir) {
    var input = $('[data-adev-quantity-value]', wrap);
    if (!input) return;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;
    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    if (isNaN(min)) min = 1;
    if (isNaN(max)) max = 1000;

    var next = current + dir;
    if (next > max || next < min) return;

    input.value = next;
    input.setAttribute('value', next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------ upsell */

  var PREV_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="1em" height="1em" viewBox="0 0 24 24"><g stroke-width="1.5" fill="none"><path d="M15 6l-6 6l6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g></svg>';
  var NEXT_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="1em" height="1em" viewBox="0 0 24 24"><g stroke-width="1.5" fill="none"><path d="M9 6l6 6l-6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g></svg>';

  /**
   * Vanilla replacement for the Slick carousel the Dawn build used.
   * Keeps Slick's class names so the ported CSS applies untouched.
   */
  function initUpsellCarousel() {
    var container = $('[data-adev-cart-drawer-upsell="true"]');
    if (!container || container.classList.contains('slick-initialized')) return;

    var slides = $$('.adev-upsell-item-col', container);
    if (!slides.length) return;

    var list = document.createElement('div');
    list.className = 'slick-list';
    var track = document.createElement('div');
    track.className = 'slick-track';

    slides.forEach(function (s) {
      s.classList.add('slick-slide');
      track.appendChild(s);
    });
    list.appendChild(track);
    container.appendChild(list);

    var prev = document.createElement('div');
    prev.className = 'slick-prev-arrow slick-arrow';
    prev.setAttribute('role', 'button');
    prev.setAttribute('tabindex', '0');
    prev.setAttribute('aria-label', 'Previous suggestion');
    prev.innerHTML = PREV_SVG;

    var next = document.createElement('div');
    next.className = 'slick-next-arrow slick-arrow';
    next.setAttribute('role', 'button');
    next.setAttribute('tabindex', '0');
    next.setAttribute('aria-label', 'Next suggestion');
    next.innerHTML = NEXT_SVG;

    container.insertBefore(prev, list);
    container.appendChild(next);
    container.classList.add('slick-initialized', 'slick-slider');

    if (slides.length < 2) {
      prev.style.display = 'none';
      next.style.display = 'none';
    }

    var index = Math.min(state.upsellIndex, slides.length - 1);
    if (index < 0) index = 0;

    function render() {
      track.style.transform = 'translate3d(' + -index * 100 + '%,0,0)';
      slides.forEach(function (s, i) {
        s.classList.toggle('slick-active', i === index);
        s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
      prev.classList.toggle('slick-disabled', index === 0);
      next.classList.toggle('slick-disabled', index >= slides.length - 1);
      state.upsellIndex = index;
    }

    function go(i) {
      index = Math.max(0, Math.min(i, slides.length - 1));
      render();
    }

    prev.addEventListener('click', function () {
      go(index - 1);
    });
    next.addEventListener('click', function () {
      go(index + 1);
    });
    [prev, next].forEach(function (btn) {
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
    });

    // Touch swipe
    var startX = null,
      startY = null,
      locked = false;
    list.addEventListener(
      'touchstart',
      function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        locked = false;
      },
      { passive: true }
    );
    list.addEventListener(
      'touchmove',
      function (e) {
        if (startX === null || locked) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          locked = true;
          go(index + (dx < 0 ? 1 : -1));
        }
      },
      { passive: true }
    );
    list.addEventListener('touchend', function () {
      startX = null;
      startY = null;
    });

    render();
  }

  function openUpsellPopup(productId) {
    var popup = $('[data-adev-upsell-popup]');
    var panel = $('[data-uspp-upsell-product="' + productId + '"]');
    if (!popup || !panel) return;
    $$('[data-uspp-upsell-product].active').forEach(function (el) {
      el.classList.remove('active');
    });
    popup.classList.add('active');
    panel.classList.add('active');
  }

  function closeUpsellPopup() {
    $$('[data-adev-upsell-popup].active').forEach(function (el) {
      el.classList.remove('active');
    });
    $$('[data-uspp-upsell-product].active').forEach(function (el) {
      el.classList.remove('active');
    });
  }

  function productDataFor(panel) {
    if (panel._adevData) return panel._adevData;
    var script = $('[data-uspp-product-json]', panel);
    if (!script) return null;
    try {
      panel._adevData = JSON.parse(script.textContent);
    } catch (e) {
      console.error('[adev-cart] bad upsell JSON', e);
      return null;
    }
    return panel._adevData;
  }

  function updateUpsellVariant(panel) {
    var data = productDataFor(panel);
    if (!data) return;

    var chosen = $$('[data-uspp-variant-option-input]', panel).map(function (s) {
      return s.value;
    });

    var variant = data.variants.filter(function (v) {
      return v.options.length === chosen.length
        ? v.options.every(function (o, i) {
            return o === chosen[i];
          })
        : false;
    })[0];

    if (!variant && data.variants.length === 1) variant = data.variants[0];
    if (!variant) return;

    var titleEl = $('[data-uspp-variant-title]', panel);
    if (titleEl) titleEl.textContent = variant.title;

    var prices = $('[data-uspp-product-prices]', panel);
    var priceEl = $('[data-uspp-regular-price]', panel);
    var compareEl = $('[data-uspp-compare-at-price]', panel);

    if (priceEl) priceEl.innerHTML = variant.price;
    if (prices) prices.setAttribute('data-on-sale', variant.on_sale ? 'true' : 'false');
    if (compareEl) {
      if (variant.on_sale && variant.compare_at_price) {
        compareEl.style.display = '';
        var s = compareEl.querySelector('s');
        if (s) s.innerHTML = variant.compare_at_price;
      } else {
        compareEl.style.display = 'none';
      }
    }

    var img = $('[data-uspp-image]', panel);
    if (img && variant.image) img.setAttribute('src', variant.image);

    var atc = $('[data-uspp-add-to-cart]', panel);
    if (atc) {
      atc.setAttribute('href', rootUrl() + '/cart/add/' + variant.id);
      atc.setAttribute('data-adev-product-id', variant.id);
      var label = atc.querySelector('span');
      if (variant.available) {
        atc.classList.remove('disabled');
        if (label) label.textContent = 'Add to Cart';
      } else {
        atc.classList.add('disabled');
        if (label) label.textContent = 'Sold Out';
      }
    }
  }

  function addUpsellToCart(link) {
    var id = link.getAttribute('data-adev-product-id');
    if (!id || link.classList.contains('loading') || link.classList.contains('disabled')) return;

    var label = link.querySelector('span');
    var original = label ? label.textContent : '';
    link.classList.add('loading');
    if (label) label.textContent = 'Adding...';
    setLoading(true);

    cartRequest('/cart/add.js', withSections({ id: id, quantity: 1 }))
      .then(function (data) {
        closeUpsellPopup();
        link.classList.remove('loading');
        if (label) label.textContent = original || 'Add to Cart';
        return refresh(data.sections);
      })
      .catch(function (e) {
        link.classList.remove('loading');
        if (label) label.textContent = original || 'Add to Cart';
        setLoading(false);
        console.error('[adev-cart] upsell add failed', e);
      });
  }

  /* ------------------------------------------------------------------ reinit */

  function reinit() {
    initUpsellCarousel();
    bindScrollTracking();
  }

  function bindScrollTracking() {
    var el = scrollEl();
    if (!el || el._adevScrollBound) return;
    el._adevScrollBound = true;
    el.addEventListener(
      'scroll',
      function () {
        state.scrollPosition = el.scrollTop;
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------- external cart writes */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var externalRefresh = debounce(function (shouldOpen) {
    refresh().then(function () {
      if (shouldOpen) openDrawer();
    });
  }, 80);

  function onExternalCartWrite(url, shouldOpen) {
    if (isDesignMode()) return;
    setLoading(true);
    externalRefresh(shouldOpen !== false && /\/cart\/add/.test(url));
  }

  function installInterceptors() {
    // fetch
    if (window.fetch && !window.fetch._adevPatched) {
      var origFetch = window.fetch;
      var patched = function (input, init) {
        var url = '';
        var own = false;
        try {
          url = typeof input === 'string' ? input : input && input.url ? input.url : '';
          if (init && init.headers) {
            var h = init.headers;
            if (typeof h.get === 'function') own = !!h.get('X-Adev');
            else own = !!(h['X-Adev'] || h['x-adev']);
          }
          if (!own && input && typeof input.headers === 'object' && input.headers.get) {
            own = !!input.headers.get('X-Adev');
          }
        } catch (e) {
          /* noop */
        }

        var promise = origFetch.apply(this, arguments);
        if (!own && url && CART_WRITE.test(url)) {
          promise
            .then(function (res) {
              if (res && res.ok) onExternalCartWrite(url);
              return res;
            })
            .catch(function () {});
        }
        return promise;
      };
      patched._adevPatched = true;
      window.fetch = patched;
    }

    // XMLHttpRequest
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && !XHR.prototype._adevPatched) {
      var origOpen = XHR.prototype.open;
      var origSend = XHR.prototype.send;
      var origSetHeader = XHR.prototype.setRequestHeader;

      XHR.prototype.open = function (method, url) {
        this._adevUrl = url;
        return origOpen.apply(this, arguments);
      };
      XHR.prototype.setRequestHeader = function (name, value) {
        if (String(name).toLowerCase() === 'x-adev') this._adevOwn = true;
        return origSetHeader.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        var self = this;
        if (!self._adevOwn && self._adevUrl && CART_WRITE.test(self._adevUrl)) {
          self.addEventListener('load', function () {
            if (self.status >= 200 && self.status < 300) onExternalCartWrite(self._adevUrl);
          });
        }
        return origSend.apply(this, arguments);
      };
      XHR.prototype._adevPatched = true;
    }
  }

  /* ------------------------------------------------------------------- events */

  function bindGlobalEvents() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // open / close
      var opener = t.closest('[data-adev-open-drawer]');
      if (opener) {
        e.preventDefault();
        openDrawer();
        return;
      }
      if (t.closest('[data-adev-close-drawer]') || t.closest('[data-adev-drawer-overlay]')) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      // remove line
      var remove = t.closest('[data-adev-remove-cart-drawer-item]');
      if (remove) {
        e.preventDefault();
        removeItem(remove.getAttribute('data-key'));
        return;
      }

      // quantity steppers
      var up = t.closest('[data-adev-quantity-up]');
      if (up) {
        e.preventDefault();
        stepQuantity(up.closest('[data-adev-quantity-selector]'), 1);
        return;
      }
      var down = t.closest('[data-adev-quantity-down]');
      if (down) {
        e.preventDefault();
        stepQuantity(down.closest('[data-adev-quantity-selector]'), -1);
        return;
      }

      // shipping protection
      var sp = t.closest('[data-adev-shipping-protection-atc]');
      if (sp) {
        e.preventDefault();
        toggleShippingProtection(sp);
        return;
      }

      // upsell: open variant popup
      var upsellAdd = t.closest('[data-adev-upsell-add]');
      if (upsellAdd) {
        e.preventDefault();
        openUpsellPopup(upsellAdd.getAttribute('data-adev-upsell-add'));
        return;
      }

      // upsell: confirm add
      var uspAtc = t.closest('[data-uspp-add-to-cart]');
      if (uspAtc) {
        e.preventDefault();
        addUpsellToCart(uspAtc);
        return;
      }

      // upsell popup backdrop
      var popup = t.closest('[data-adev-upsell-popup]');
      if (popup && t === popup) {
        closeUpsellPopup();
        return;
      }

      // checkout button feedback
      var checkout = t.closest('[data-adev-checkout]');
      if (checkout) {
        setLoading(true);
      }
    });

    // quantity input changes
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name.indexOf('updates[') !== 0) return;
      if (!t.closest('[data-adev-cart-drawer-form]')) return;
      setLoading(true);
      pushQuantities();
    });

    // upsell variant selects
    document.addEventListener('change', function (e) {
      var sel = e.target && e.target.closest && e.target.closest('[data-uspp-variant-option-input]');
      if (!sel) return;
      var panel = sel.closest('[data-uspp-upsell-product]');
      if (panel) updateUpsellVariant(panel);
    });

    // esc + focus trap
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        if ($('[data-adev-upsell-popup].active')) {
          closeUpsellPopup();
        } else {
          closeDrawer();
        }
        return;
      }
      if (e.key === 'Tab') {
        var d = drawerEl();
        if (!d) return;
        var focusables = $$(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
          d
        ).filter(function (el) {
          return el.offsetParent !== null;
        });
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    window.addEventListener('resize', debounce(measureFooter, 150));

    // Horizon's own cart events (product form, quick add, etc.)
    document.addEventListener('cart:update', function (e) {
      var src = e && e.detail && e.detail.data && e.detail.data.source;
      if (src === SECTION_ID) return; // our own echo
      if (isDesignMode()) return;
      setLoading(true);
      externalRefresh(true);
    });

    document.addEventListener('cart:error', function () {
      setLoading(false);
    });
  }

  /* -------------------------------------------------------- theme editor hooks */

  function bindEditorEvents() {
    function isOurSection(target) {
      try {
        return (
          target &&
          target.dataset &&
          JSON.parse(target.dataset.shopifyEditorSection).type === SECTION_ID
        );
      } catch (e) {
        return false;
      }
    }
    document.addEventListener('shopify:section:load', function (e) {
      if (!isOurSection(e.target)) return;
      reinit();
      measureFooter();
    });
    document.addEventListener('shopify:section:select', function (e) {
      if (isOurSection(e.target)) openDrawer();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (isOurSection(e.target)) closeDrawer();
    });
  }

  /* -------------------------------------------------------------------- boot */

  function init() {
    if (!drawerEl()) return;
    bindGlobalEvents();
    bindEditorEvents();
    installInterceptors();
    reinit();
    measureFooter();
    syncShippingProtectionState();

    window.adev = window.adev || {};
    window.adev.cartDrawer = {
      open: openDrawer,
      close: closeDrawer,
      refresh: refresh,
      state: state
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
